import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { Security2FAType } from '@/shared/types/auth';

type UserTwoFactorAuthMethod = {
  /**
   * The two-factor authentication method the user has set up.
   */
  twoFactorAuthMethod: Security2FAType | null;
  /**
   * Whether the user is required to use two-factor
   * authentication when signing in.
   */
  isTwoFactorRequired: boolean | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
};

/**
 * Get the user's two-factor authentication method and whether
 * they are required to use it when signing in.
 *
 * @example
 * ```tsx
 * const { twoFactorAuthMethod, isTwoFactorRequired, isLoading } = useGetUsersTwoFactorAuthMethod();
 *
 * if (isLoading) {
 *   return <div>Loading...</div>;
 * }
 *
 * if (twoFactorAuthMethod === Security2FAType.NONE) {
 *   return <div>Two-factor authentication is not enabled.</div>;
 * }
 *
 * const methodName = twoFactorAuthMethod === Security2FAType.AUTHENTICATION_APP ?
 *   'Authentication App' :
 *   twoFactorAuthMethod === Security2FAType.SMS ?
 *     'SMS Text Message' :
 *     'Unknown';
 *
 * return (<div>
 *   <p>Two-factor authentication method: {methodName}</p>
 *   <p>Is two-factor required: {isTwoFactorRequired ? 'Yes' : 'No'}</p>
 * </div>);
 * ```
 */
export function useGetUsersTwoFactorAuthMethod(): UserTwoFactorAuthMethod {
  const { user, isLoading, isError, error } = useGetCurrentUser();

  const twoFactorAuthMethod =
    user?.two_factor?.two_factor_type ?? Security2FAType.NONE;
  const isTwoFactorRequired = user?.two_factor?.two_factor_required ?? null;

  return {
    twoFactorAuthMethod,
    isTwoFactorRequired,
    isLoading,
    isError,
    error,
  };
}
